import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { opsFetch } from "../lib/api";
import { Count, Field, Pill, Thumb } from "../components/ui";
import { ExperiencePreview } from "../components/PreviewPane";
import { SplitPane } from "../components/SplitPane";
import { downloadCsv } from "../lib/exportCsv";

type Listing = {
  slug: string;
  merchant_slug: string;
  kind: string;
  status: string;
  city_slug: string;
  category: string;
  title_en: string;
  title_zh: string;
  blurb_en: string;
  blurb_zh: string;
  image_src: string;
  price_from_cny: number | null;
  book_url: string | null;
};

const EMPTY: Listing = {
  slug: "",
  merchant_slug: "klook",
  kind: "experience",
  status: "draft",
  city_slug: "",
  category: "sightseeing",
  title_en: "",
  title_zh: "",
  blurb_en: "",
  blurb_zh: "",
  image_src: "",
  price_from_cny: null,
  book_url: "",
};

export function ListingsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Listing | null>(null);
  const [shelf, setShelf] = useState("all");
  const [kind, setKind] = useState("");
  const [qtext, setQtext] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["ops-listings"],
    queryFn: () => opsFetch<{ data: { items: Listing[] } }>("/api/ops/listings"),
  });
  const save = useMutation({
    mutationFn: (body: Listing) =>
      opsFetch(`/api/ops/listings/${encodeURIComponent(body.slug.trim().toLowerCase())}`, {
        method: "PUT",
        body: JSON.stringify({ ...body, slug: body.slug.trim().toLowerCase(), book_url: body.book_url || null }),
      }),
    onSuccess: () => {
      setMsg("Saved. A published experience updates the public card on the next site build.");
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["ops-listings"] });
    },
    onError: (e: Error) => setMsg(e.message),
  });
  const archive = useMutation({
    mutationFn: (slug: string) => opsFetch(`/api/ops/listings/${encodeURIComponent(slug)}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ops-listings"] }),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    save.mutate(editing);
  }

  const items = q.data?.data.items ?? [];
  const needle = qtext.trim().toLowerCase();
  const shown = items.filter((item) => {
    const gaps = listingGaps(item);
    if (kind && item.kind !== kind) return false;
    if (shelf === "live" && !(item.status === "publish" && gaps.length === 0)) return false;
    if (shelf === "blocked" && !(item.status !== "archived" && gaps.length > 0)) return false;
    if (shelf === "draft" && item.status !== "draft") return false;
    if (!needle) return true;
    return `${item.title_en} ${item.title_zh} ${item.city_slug} ${item.category}`.toLowerCase().includes(needle);
  });
  const current = (editing ?? shown.find((item) => item.slug === picked) ?? shown[0]) || null;

  return (
    <SplitPane>
      <div>
      {msg ? <p className="ops-ok">{msg}</p> : null}
      <div className="ops-toolbar">
        <button className="ops-btn ops-btn--primary" onClick={() => setEditing({ ...EMPTY })} type="button">
          New listing
        </button>
        <select className="ops-input" onChange={(e) => setShelf(e.target.value)} value={shelf}>
          <option value="all">All</option>
          <option value="blocked">Cannot publish</option>
          <option value="live">On the shelf</option>
          <option value="draft">Draft</option>
        </select>
        <select className="ops-input" onChange={(e) => setKind(e.target.value)} value={kind}>
          <option value="">All kinds</option>
          <option value="experience">experience</option>
          <option value="food">food</option>
          <option value="sight">sight</option>
        </select>
        <input className="ops-input" onChange={(e) => setQtext(e.target.value)} placeholder="Search title or city" value={qtext} />
        <button
          className="ops-btn"
          onClick={() =>
            downloadCsv(
              "listings.csv",
              ["slug", "title", "kind", "category", "city", "status"],
              shown.map((item) => [item.slug, item.title_en, item.kind, item.category, item.city_slug, item.status]),
            )
          }
          type="button"
        >
          Export
        </button>
        {q.data ? <Count label="shown" n={shown.length} /> : null}
      </div>
      {editing ? (
        <form className="ops-panel ops-form" onSubmit={onSubmit}>
          <h3 className="ops-panel__title">{editing.title_en ? "Edit listing" : "New listing"}</h3>
          <div className="ops-form__grid">
            <Field label="Slug">
              <input className="ops-input" onChange={(e) => setEditing({ ...editing, slug: e.target.value })} required value={editing.slug} />
            </Field>
            <Field label="Merchant slug">
              <input className="ops-input" onChange={(e) => setEditing({ ...editing, merchant_slug: e.target.value })} required value={editing.merchant_slug} />
            </Field>
            <Field label="Kind">
              <select className="ops-input" onChange={(e) => setEditing({ ...editing, kind: e.target.value })} value={editing.kind}>
                <option value="experience">experience</option>
                <option value="food">food</option>
                <option value="sight">sight</option>
              </select>
            </Field>
            <Field label="Status">
              <select className="ops-input" onChange={(e) => setEditing({ ...editing, status: e.target.value })} value={editing.status}>
                <option value="draft">draft</option>
                <option value="publish">publish</option>
                <option value="archived">archived</option>
              </select>
            </Field>
            <Field label="City slug">
              <input className="ops-input" onChange={(e) => setEditing({ ...editing, city_slug: e.target.value })} value={editing.city_slug} />
            </Field>
            <Field label="English title">
              <input className="ops-input" onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} required value={editing.title_en} />
            </Field>
            <Field label="中文标题">
              <input className="ops-input" onChange={(e) => setEditing({ ...editing, title_zh: e.target.value })} value={editing.title_zh} />
            </Field>
            <Field label="Book URL">
              <input className="ops-input" onChange={(e) => setEditing({ ...editing, book_url: e.target.value })} value={editing.book_url || ""} />
            </Field>
            <Field label="Image URL">
              <input className="ops-input" onChange={(e) => setEditing({ ...editing, image_src: e.target.value })} value={editing.image_src} />
            </Field>
          </div>
          <div className="ops-toolbar">
            <button className="ops-btn ops-btn--primary" type="submit">Save</button>
            <button className="ops-btn ops-btn--ghost" onClick={() => setEditing(null)} type="button">Cancel</button>
          </div>
        </form>
      ) : null}
      {q.data ? (
        <div className="ops-table-wrap ops-panel">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>City</th>
                <th>Shelf</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((item) => {
                const gaps = listingGaps(item);
                return (
                  <tr className={current?.slug === item.slug ? "is-selected" : undefined} key={item.slug} onClick={() => setPicked(item.slug)}>
                    <td>
                      <div className="ops-row-main">
                        <Thumb src={item.image_src} />
                        <div className="ops-row-copy">
                          <strong>{item.title_en}</strong>
                          <span className="ops-tags">
                            <Pill value={item.kind} />
                            {item.category ? <span className="ops-muted">{item.category}</span> : null}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>{item.city_slug || "—"}</td>
                    <td>{gaps.length ? <span className="ops-gap">Missing {gaps.join(", ")}</span> : <span className="ops-ready">Can publish</span>}</td>
                    <td><Pill value={item.status} /></td>
                    <td>
                      <button className="ops-btn ops-btn--ghost" type="button" onClick={() => setEditing(item)}>Edit</button>
                      {item.status !== "archived" ? (
                        <button className="ops-btn ops-btn--ghost" type="button" onClick={() => archive.mutate(item.slug)}>
                          Archive
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      </div>
      <ExperiencePreview
        item={{
          title: current?.title_en || "",
          titleZh: current?.title_zh,
          blurb: current?.blurb_en,
          image: current?.image_src,
          city: current?.city_slug,
          category: current?.category,
          kind: current?.kind,
          price: current?.price_from_cny,
          status: current?.status,
          bookUrl: current?.book_url || "",
        }}
      />
    </SplitPane>
  );
}

function listingGaps(item: Listing) {
  const gaps: string[] = [];
  if (!item.book_url) gaps.push("book link");
  if (!item.image_src) gaps.push("image");
  return gaps;
}
